import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Plus,
  Trash2,
  ArrowLeft,
  Menu,
  X,
  Bot,
  Copy,
  Check,
  Stethoscope,
  AlertTriangle,
  FlaskConical,
  Activity,
  Pill,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import {
  sendInfirmierMessage,
  loadInfirmierConversation,
  listInfirmierConversations,
  deleteInfirmierConversation,
} from '../../Services/infirmierChatService';

// ─── Suggestions rapides ──────────────────────────────
const QUICK_SUGGESTIONS = [
  { icon: Pill, label: 'Calculer une dose de paracétamol pour un enfant de 15 kg' },
  { icon: Activity, label: "Signes d'alerte sepsis à surveiller" },
  { icon: Stethoscope, label: 'Protocole de soins pour une plaie chirurgicale' },
  { icon: FlaskConical, label: "Préparation d'une perfusion de NaCl 0.9%" },
];

// ─── Helpers ──────────────────────────────────────────
const formatTime = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatRelativeDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const days = Math.floor((now - date) / 86400000);
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return 'Hier';
  if (days < 7) return `Il y a ${days}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const ChatInfirmierPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  // ─── États ────────────────────────────────────────────
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // ─── Auto-resize textarea ─────────────────────────────
  useEffect(() => {
    const ta = inputRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    }
  }, [inputMessage]);

  // ─── Charger la liste des conversations ──────────────
  useEffect(() => {
    loadConversationsList();
  }, []);

  // ─── Auto-scroll vers le bas ─────────────────────────
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const loadConversationsList = async () => {
    try {
      setIsLoadingConversations(true);
      const convos = await listInfirmierConversations();
      setConversations(convos || []);
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // ─── Nouvelle conversation ────────────────────────────
  const handleNewConversation = () => {
    setCurrentConversationId(`conv_${Date.now()}`);
    setMessages([]);
    inputRef.current?.focus();
  };

  // ─── Charger une conversation ─────────────────────────
  const handleSelectConversation = async (conversationId) => {
    try {
      setIsLoading(true);
      const conversation = await loadInfirmierConversation(conversationId);
      setCurrentConversationId(conversationId);
      setMessages(conversation.messages || []);
      if (window.innerWidth < 1024) setIsSidebarOpen(false);
    } catch (error) {
      console.error('Erreur chargement conversation:', error);
      toast.error('Erreur lors du chargement de la conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Supprimer une conversation ───────────────────────
  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette conversation ?')) return;

    try {
      await deleteInfirmierConversation(conversationId);
      setConversations(conversations.filter((c) => c.id !== conversationId));
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      toast.success('Conversation supprimée');
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // ─── Copier un message ────────────────────────────────
  const handleCopy = useCallback(async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      toast.error('Impossible de copier');
    }
  }, []);

  // ─── Envoyer un message ───────────────────────────────
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await sendInfirmierMessage(
        userMessage,
        currentConversationId,
        conversationHistory
      );

      const assistantMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      loadConversationsList();
    } catch (error) {
      console.error('Erreur envoi message:', error);
      toast.error("Erreur lors de l'envoi du message");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // ─── Initiale utilisateur ─────────────────────────────
  const userInitial = user?.displayName
    ? user.displayName.charAt(0).toUpperCase()
    : (user?.email?.charAt(0).toUpperCase() ?? 'U');

  // ─── Render ────────────────────────────────────────────
  return (
    <div className={`h-screen flex overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <style>{`
        .ai-message ul > li::marker { color: #14b8a6; }
        .ai-message ul ul > li::marker { color: #60a5fa; }
        .dark .ai-message ul > li::marker { color: #2dd4bf; }
        .dark .ai-message ul ul > li::marker { color: #93c5fd; }
      `}</style>
      {/* ══════════════════════════════════════════════════ */}
      {/* OVERLAY MOBILE */}
      {/* ══════════════════════════════════════════════════ */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* ══════════════════════════════════════════════════ */}
      {/* SIDEBAR */}
      {/* ══════════════════════════════════════════════════ */}
      <aside
        className={`
          ${isSidebarOpen ? 'w-80' : 'w-0'}
          ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border-r transition-all duration-300 flex flex-col shrink-0
          overflow-hidden z-30 lg:relative fixed inset-y-0 left-0
        `}
      >
        {/* Header Sidebar */}
        <div className={`p-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversations
            </h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`
                p-1.5 rounded-lg transition-colors
                ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}
              `}
            >
              <X size={18} />
            </button>
          </div>

          <button
            onClick={handleNewConversation}
            className={`
              w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
              font-medium text-sm transition-all active:scale-95
              ${
                isDark
                  ? 'bg-teal-600 hover:bg-teal-500 text-white'
                  : 'bg-teal-500 hover:bg-teal-600 text-white'
              }
            `}
          >
            <Plus size={18} />
            <span>Nouvelle conversation</span>
          </button>
        </div>

        {/* Liste des conversations */}
        <div className="flex-1 overflow-y-auto p-3">
          {isLoadingConversations ? (
            <div className="space-y-2 mt-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`h-14 rounded-lg animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}
                />
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center py-12 gap-3">
              <MessageSquare className={isDark ? 'text-gray-600' : 'text-gray-300'} size={32} />
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Aucune conversation
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv.id)}
                  className={`
                    group relative p-3 rounded-xl cursor-pointer transition-all
                    ${
                      currentConversationId === conv.id
                        ? isDark
                          ? 'bg-teal-600/20 border border-teal-600/40'
                          : 'bg-teal-50 border border-teal-200'
                        : isDark
                          ? 'hover:bg-gray-700/70 border border-transparent'
                          : 'hover:bg-gray-50 border border-transparent'
                    }
                  `}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
                      >
                        {conv.title}
                      </p>
                      <div
                        className={`flex items-center gap-2 mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                      >
                        <span>
                          {conv.messageCount} msg{conv.messageCount > 1 ? 's' : ''}
                        </span>
                        {conv.updatedAt && (
                          <>
                            <span>·</span>
                            <span>{formatRelativeDate(conv.updatedAt)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => handleDeleteConversation(conv.id, e)}
                      className={`
                        opacity-0 group-hover:opacity-100 transition-opacity shrink-0
                        p-1.5 rounded-lg hover:bg-red-500/20
                        ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}
                      `}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════ */}
      {/* ZONE CHAT PRINCIPALE */}
      {/* ══════════════════════════════════════════════════ */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className={`
          px-4 py-3 border-b flex items-center justify-between shrink-0
          ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        `}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen((v) => !v)}
              className={`
                p-2 rounded-lg transition-colors
                ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}
              `}
            >
              <Menu size={20} />
            </button>

            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${isDark ? 'bg-teal-500/20' : 'bg-teal-50'}`}>
                <MessageSquare className="text-teal-600 dark:text-teal-400" size={20} />
              </div>
              <div>
                <h1
                  className={`text-base font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  Chat Clinique IA
                </h1>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Assistant médical professionnel
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Badge statut */}
            <div
              className={`
              hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
              ${isDark ? 'bg-teal-500/10 text-teal-400' : 'bg-teal-50 text-teal-700'}
            `}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              En ligne
            </div>

            <button
              onClick={() => navigate(-1)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm
                ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}
              `}
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Retour</span>
            </button>
          </div>
        </header>

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
            {/* BANDEAU DISCLAIMER DISCRET (Nouveau) */}
            {messages.length > 0 && (
              <div
                className={`
        flex items-center gap-3 p-3 mb-6 rounded-xl border
        ${isDark ? 'bg-amber-500/5 border-amber-500/20 text-amber-200/70' : 'bg-amber-50 border-amber-200 text-amber-800'}
      `}
              >
                <AlertTriangle size={16} className="shrink-0 text-amber-500" />
                <p className="text-[11px] leading-tight font-medium">
                  Aide-mémoire clinique : les réponses doivent être validées par votre expertise et
                  les protocoles de votre établissement.
                </p>
              </div>
            )}
            {/* ── Écran vide ── */}
            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center py-12 gap-6">
                <div
                  className={`
                  p-5 rounded-2xl
                  ${isDark ? 'bg-gray-800' : 'bg-white shadow-sm border border-gray-100'}
                `}
                >
                  <Bot className={isDark ? 'text-teal-400' : 'text-teal-600'} size={40} />
                </div>

                <div className="text-center">
                  <h3
                    className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
                  >
                    Bienvenue dans le Chat Clinique IA
                  </h3>
                  <p
                    className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} max-w-md mx-auto`}
                  >
                    Posez vos questions cliniques, demandez des protocoles, calculez des doses ou
                    discutez de cas complexes.
                  </p>
                </div>

                {/* Suggestions rapides */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
                  {QUICK_SUGGESTIONS.map(({ icon: Icon, label }) => (
                    <button
                      key={label}
                      onClick={() => {
                        setInputMessage(label);
                        inputRef.current?.focus();
                      }}
                      className={`
                        flex items-start gap-3 p-3.5 rounded-xl text-left text-sm transition-all
                        border hover:scale-[1.01] active:scale-[0.99]
                        ${
                          isDark
                            ? 'bg-gray-800 border-gray-700 hover:border-teal-600/50 text-gray-300'
                            : 'bg-white border-gray-200 hover:border-teal-300 text-gray-700 shadow-sm'
                        }
                      `}
                    >
                      <Icon
                        size={16}
                        className={`mt-0.5 shrink-0 ${isDark ? 'text-teal-400' : 'text-teal-600'}`}
                      />
                      <span className="leading-snug">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Messages ── */}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {/* Avatar assistant */}
                {message.role === 'assistant' && (
                  <div
                    className={`
                    shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1
                    ${isDark ? 'bg-teal-500/20' : 'bg-teal-50'}
                  `}
                  >
                    <Bot size={16} className={isDark ? 'text-teal-400' : 'text-teal-600'} />
                  </div>
                )}

                <div
                  className={`flex flex-col gap-1 max-w-[80%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`
                      px-5 py-4 rounded-2xl
                      ${
                        message.role === 'user'
                          ? isDark
                            ? 'bg-teal-600 text-white rounded-tr-sm'
                            : 'bg-teal-500 text-white rounded-tr-sm'
                          : isDark
                            ? 'bg-gray-800 text-gray-100 border border-gray-700 rounded-tl-sm'
                            : 'bg-white text-gray-900 shadow-sm border border-gray-100 rounded-tl-sm'
                      }
                    `}
                  >
                    {message.role === 'assistant' ? (
                      <div className="ai-message prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({node, ...props}) => (
                              <h1 className={`text-base font-bold mt-5 mb-3 pb-1.5 border-b ${isDark ? 'border-gray-700 text-teal-300' : 'border-gray-200 text-teal-700'}`} {...props} />
                            ),
                            h2: ({node, ...props}) => (
                              <h2 className={`text-sm font-bold mt-5 mb-2.5 pb-1 border-b ${isDark ? 'border-gray-700/60 text-teal-300' : 'border-gray-200/80 text-teal-700'}`} {...props} />
                            ),
                            h3: ({node, ...props}) => (
                              <h3 className={`text-sm font-semibold mt-4 mb-2 ${isDark ? 'text-teal-400' : 'text-teal-600'}`} {...props} />
                            ),
                            strong: ({node, ...props}) => (
                              <strong className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />
                            ),
                            ul: ({node, ...props}) => (
                              <ul className="list-disc pl-5 my-3 space-y-1.5" {...props} />
                            ),
                            ol: ({node, ...props}) => (
                              <ol className="list-decimal pl-5 my-3 space-y-1.5" {...props} />
                            ),
                            li: ({node, ...props}) => (
                              <li className="text-sm leading-relaxed pl-1" {...props} />
                            ),
                            p: ({node, ...props}) => (
                              <p className="text-sm leading-7 my-3 first:mt-0 last:mb-0" {...props} />
                            ),
                            hr: ({node, ...props}) => (
                              <hr className={`my-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} {...props} />
                            ),
                            blockquote: ({node, ...props}) => (
                              <blockquote className={`border-l-4 border-teal-500 pl-4 py-2 my-3 rounded-r-lg text-sm italic ${isDark ? 'bg-teal-900/20 text-gray-300' : 'bg-teal-50 text-gray-700'}`} {...props} />
                            ),
                            code: ({node, inline, ...props}) => inline ? (
                              <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${isDark ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700'}`} {...props} />
                            ) : (
                              <code className={`block p-3 rounded-lg text-xs font-mono my-3 overflow-x-auto ${isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-800'}`} {...props} />
                            ),
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {message.content}
                      </p>
                    )}
                  </div>

                  {/* Timestamp + bouton copier */}
                  <div
                    className={`flex items-center gap-2 px-1 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {formatTime(message.timestamp)}
                    </span>

                    {message.role === 'assistant' && (
                      <button
                        onClick={() => handleCopy(message.content, index)}
                        className={`
                          p-1 rounded transition-colors
                          ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-500'}
                        `}
                        title="Copier"
                      >
                        {copiedIndex === index ? (
                          <Check size={13} className="text-teal-500" />
                        ) : (
                          <Copy size={13} />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Avatar utilisateur */}
                {message.role === 'user' && (
                  <div
                    className={`
                    shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1
                    bg-teal-600 text-white text-xs font-bold
                  `}
                  >
                    {userInitial}
                  </div>
                )}
              </div>
            ))}

            {/* ── Indicateur de chargement ── */}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div
                  className={`
                  shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                  ${isDark ? 'bg-teal-500/20' : 'bg-teal-50'}
                `}
                >
                  <Bot size={16} className={isDark ? 'text-teal-400' : 'text-teal-600'} />
                </div>
                <div
                  className={`
                  px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-3
                  ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-sm border border-gray-100'}
                `}
                >
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className={`w-2 h-2 rounded-full ${isDark ? 'bg-gray-500' : 'bg-gray-300'} animate-bounce`}
                        style={{ animationDelay: `${i * 150}ms` }}
                      />
                    ))}
                  </div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Réflexion en cours…
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ── Zone de saisie ── */}
        <div
          className={`
          px-4 py-4 border-t shrink-0
          ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        `}
        >
          <form onSubmit={handleSendMessage} className="max-w-3xl mx-auto">
            <div
              className={`
              flex items-end gap-3 px-4 py-3 rounded-2xl transition-all
              ${
                inputFocused
                  ? isDark
                    ? 'ring-2 ring-teal-500/50 bg-gray-900'
                    : 'ring-2 ring-teal-400/50 bg-gray-50'
                  : isDark
                    ? 'bg-gray-900'
                    : 'bg-gray-50'
              }
            `}
            >
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Posez votre question clinique…"
                rows={1}
                className={`
                  flex-1 resize-none bg-transparent outline-none text-sm leading-relaxed
                  ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}
                `}
                style={{ minHeight: '22px', maxHeight: '200px' }}
              />

              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className={`
                  p-2.5 rounded-xl transition-all active:scale-95
                  ${
                    inputMessage.trim() && !isLoading
                      ? isDark
                        ? 'bg-teal-600 hover:bg-teal-500 text-white'
                        : 'bg-teal-500 hover:bg-teal-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                <Send size={18} />
              </button>
            </div>

            {/* ZONE DISCLAIMER : Bien centrée sous la barre de saisie */}
          <div className="flex flex-col items-center mt-3 gap-1">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>
              Usage Professionnel Uniquement
            </p>
            <p className={`text-[11px] text-center leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Entrée pour envoyer · Maj+Entrée pour sauter une ligne <br className="sm:hidden" />
              · L'IA peut générer des erreurs. Vérifiez systématiquement les doses.
            </p>
          </div>
          
          </form>
        </div>
      </main>
    </div>
  );
};

export default ChatInfirmierPage;
